import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Save, Eye, EyeOff, User, Mail, Phone, Shield, Check, X, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { User as UserType, Role } from '@/types/auth';
import { useAdminUsers, useUpdateAdminUser } from '@/api-services/hooks/useAdminUsers';
import { UserDTO } from '@/api-services/hooks/adminTypes';

interface EditUserForm {
  userName: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  password: string;
  confirmPassword: string;
  roles: string[];
  status: string;
  activated: boolean;
}

const mockRoles: Role[] = [
  {
    id: '1',
    name: 'Super Admin',
    description: 'Full system access with all permissions',
    isSystemRole: true,
    permissions: [],
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T10:00:00Z'
  },
  {
    id: '2',
    name: 'Admin',
    description: 'Administrative access to most features',
    isSystemRole: true,
    permissions: [],
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T10:00:00Z'
  },
  {
    id: '3',
    name: 'Driver',
    description: 'Driver role with limited access',
    isSystemRole: true,
    permissions: [],
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T10:00:00Z'
  },
  {
    id: '4',
    name: 'Passenger',
    description: 'Passenger role with ride-related access',
    isSystemRole: true,
    permissions: [],
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T10:00:00Z'
  }
];

// Role mapping helper
const roleNameToApiRole: Record<string, string> = {
  'Super Admin': 'ROLE_SUPER_ADMIN',
  'Admin': 'ROLE_ADMIN',
  'Driver': 'ROLE_DRIVER',
  'Passenger': 'ROLE_PASSENGER',
};

const apiRoleToRoleName: Record<string, string> = {
  'ROLE_SUPER_ADMIN': 'Super Admin',
  'ROLE_ADMIN': 'Admin',
  'ROLE_DRIVER': 'Driver',
  'ROLE_PASSENGER': 'Passenger',
  'ROLE_USER': 'Passenger', // Map ROLE_USER to Passenger for compatibility
};

const EditUserPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams<{ userId: string }>();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [userDTO, setUserDTO] = useState<UserDTO | null>(null);
  const [fetchingUser, setFetchingUser] = useState(true);

  const [formData, setFormData] = useState<EditUserForm>({
    userName: '',
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: '',
    roles: [],
    status: 'active',
    activated: true,
  });

  // Check if user data was passed via navigation state
  const passedUser = location.state?.user as UserType | undefined;

  // Convert UI User to UserDTO format
  const convertUserToDTO = (user: UserType): UserDTO => {
    const userId = user.companyUserId || parseInt(user.id);
    return {
      id: userId,
      companyUserId: userId,
      userName: user.userName || user.email.split('@')[0],
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      mobile: user.phone || user.phoneNumber,
      roles: user.roles,
      status: user.status,
      activated: user.activated,
    };
  };

  // Fetch user data from API as fallback
  const usersQuery = useAdminUsers(
    {
      page: 0,
      size: 1000, // Get all users to find the specific one
    },
    {
      enabled: !passedUser && !!userId, // Only fetch if no user data was passed
      onSuccess: (data) => {
        console.log('Fetched users data:', data);
        
        // Find the user with matching ID
        const foundUser = data.content.find(
          (u) => String(u.id) === userId || String(u.companyUserId) === userId
        );
        
        if (foundUser) {
          console.log('Found user:', foundUser);
          setUserDTO(foundUser);
          
          // Map API data to form
          setFormData({
            userName: foundUser.userName || '',
            firstName: foundUser.firstName || '',
            lastName: foundUser.lastName || '',
            email: foundUser.email || '',
            mobile: foundUser.mobile || '',
            password: '',
            confirmPassword: '',
            roles: foundUser.roles || [],
            status: foundUser.status || 'active',
            activated: foundUser.activated !== undefined ? foundUser.activated : true,
          });
          setFetchingUser(false);
        } else {
          console.error('User not found in list');
          toast.error('User not found');
          navigate('/admin/users/list');
        }
      },
      onError: (error) => {
        console.error('Failed to fetch users:', error);
        toast.error('Failed to fetch user details');
        navigate('/admin/users/list');
      },
    }
  );

  // Load user data from passed state
  useEffect(() => {
    if (passedUser) {
      console.log('Using passed user data:', passedUser);
      const dto = convertUserToDTO(passedUser);
      setUserDTO(dto);
      
      setFormData({
        userName: dto.userName || '',
        firstName: dto.firstName || '',
        lastName: dto.lastName || '',
        email: dto.email || '',
        mobile: dto.mobile || '',
        password: '',
        confirmPassword: '',
        roles: dto.roles || [],
        status: dto.status || 'active',
        activated: dto.activated !== undefined ? dto.activated : true,
      });
      setFetchingUser(false);
    }
  }, [passedUser]);

  // Update user mutation
  const updateUserMutation = useUpdateAdminUser({
    onSuccess: (response) => {
      console.log('User updated successfully:', response);
      toast.success('User updated successfully!');
      navigate('/admin/users/list');
    },
    onError: (error) => {
      console.error('Failed to update user:', error);
      toast.error('Failed to update user');
    },
  });

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.userName.trim()) {
      newErrors.userName = 'Username is required';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Phone number is required';
    } else if (!/^\+?[\d\s\-\(\)]+$/.test(formData.mobile)) {
      newErrors.mobile = 'Invalid phone number format';
    }

    // Only validate password if user is trying to change it
    if (formData.password || formData.confirmPassword) {
      if (formData.password && formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters long';
      }

      if (formData.password && !formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm new password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    if (!formData.roles || formData.roles.length === 0) {
      newErrors.roles = 'At least one role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!userId || !userDTO) {
      toast.error('User ID is missing');
      return;
    }

    // Build update payload
    const updateData: UserDTO = {
      ...userDTO,
      userName: formData.userName,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      mobile: formData.mobile,
      roles: formData.roles,
      status: formData.status,
      activated: formData.activated,
    };

    // Only include password if it was changed
    if (formData.password) {
      updateData.password = formData.password;
    }

    console.log('Updating user with data:', updateData);

    updateUserMutation.mutate({
      id: parseInt(userId),
      data: updateData,
    });
  };

  const handleInputChange = (field: keyof EditUserForm, value: string | boolean | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleRoleChange = (roleName: string) => {
    const apiRole = roleNameToApiRole[roleName];
    if (apiRole) {
      setFormData(prev => ({ ...prev, roles: [apiRole] }));
      if (errors.roles) {
        setErrors(prev => ({ ...prev, roles: '' }));
      }
    }
  };

  const getSelectedRoleName = (): string => {
    if (!formData.roles || formData.roles.length === 0) return '';
    const apiRole = formData.roles[0];
    return apiRoleToRoleName[apiRole] || '';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'inactive': return 'secondary';
      case 'suspended': return 'destructive';
      case 'pending': return 'outline';
      default: return 'default';
    }
  };

  const loading = updateUserMutation.loading;

  if (fetchingUser) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/users/list')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Users
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Loading User...</h1>
          </div>
        </div>
      </div>
    );
  }

  if (!userDTO) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/users/list')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Users
          </Button>
          <div>
            <h1 className="text-3xl font-bold">User Not Found</h1>
            <p className="text-muted-foreground">The user you're trying to edit doesn't exist.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin/users/list')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Users
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Edit User</h1>
          <p className="text-muted-foreground">
            Update user account information and permissions
          </p>
        </div>
      </div>

      {/* User Info Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>User Summary</span>
            <Badge variant={getStatusColor(formData.status)}>
              {formData.activated ? 'Active' : 'Inactive'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">User ID:</span>
              <p className="font-mono">{userId}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Username:</span>
              <p>{userDTO.userName}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Last Login:</span>
              <p>{formatDate(userDTO.lastLoginDate)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Created By:</span>
              <p>{userDTO.createdBy || 'System'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Modified By:</span>
              <p>{userDTO.modifiedBy || 'N/A'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>User Information</CardTitle>
            <CardDescription>
              Update the basic information for the user account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Username and Name Fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="userName" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Username *
                </Label>
                <Input
                  id="userName"
                  value={formData.userName}
                  onChange={(e) => handleInputChange('userName', e.target.value)}
                  placeholder="Enter username"
                  className={errors.userName ? 'border-red-500' : ''}
                />
                {errors.userName && (
                  <p className="text-sm text-red-500">{errors.userName}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="firstName" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  First Name *
                </Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  placeholder="Enter first name"
                  className={errors.firstName ? 'border-red-500' : ''}
                />
                {errors.firstName && (
                  <p className="text-sm text-red-500">{errors.firstName}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Last Name *
                </Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  placeholder="Enter last name"
                  className={errors.lastName ? 'border-red-500' : ''}
                />
                {errors.lastName && (
                  <p className="text-sm text-red-500">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Email and Phone Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email Address *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="user@example.com"
                  className={errors.email ? 'border-red-500' : ''}
                />
                {errors.email && (
                  <p className="text-sm text-red-500">{errors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="mobile" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Phone Number *
                </Label>
                <Input
                  id="mobile"
                  value={formData.mobile}
                  onChange={(e) => handleInputChange('mobile', e.target.value)}
                  placeholder="+1234567890"
                  className={errors.mobile ? 'border-red-500' : ''}
                />
                {errors.mobile && (
                  <p className="text-sm text-red-500">{errors.mobile}</p>
                )}
              </div>
            </div>

            {/* Role Field */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="role" className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Role *
                </Label>
                <Select
                  value={getSelectedRoleName()}
                  onValueChange={handleRoleChange}
                >
                  <SelectTrigger className={errors.roles ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Select user role" />
                  </SelectTrigger>
                  <SelectContent>
                    {mockRoles.map((role) => (
                      <SelectItem key={role.id} value={role.name}>
                        {role.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.roles && (
                  <p className="text-sm text-red-500">{errors.roles}</p>
                )}
              </div>
            </div>

            {/* Password Fields */}
            <div className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Leave password fields empty to keep the current password unchanged
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder="Enter new password (optional)"
                      className={errors.password ? 'border-red-500 pr-10' : 'pr-10'}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  {errors.password && (
                    <p className="text-sm text-red-500">{errors.password}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <div className="relative">
                    <Input
                      id="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      placeholder="Confirm new password"
                      className={errors.confirmPassword ? 'border-red-500 pr-10' : 'pr-10'}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-sm text-red-500">{errors.confirmPassword}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Status and Activation */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="status">Account Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => handleInputChange('status', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center space-x-2 mt-6">
                  <Checkbox
                    id="activated"
                    checked={formData.activated}
                    onCheckedChange={(checked) => handleInputChange('activated', checked === true)}
                  />
                  <Label htmlFor="activated" className="flex items-center gap-2">
                    {formData.activated ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <X className="h-4 w-4 text-gray-400" />
                    )}
                    Account Activated
                  </Label>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/users/list')}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Updating User...' : 'Update User'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditUserPage;
